import React from 'react';
import {
  Button,
  Checkbox,
  Group,
  Modal as UiModal,
  Skeleton,
  Stack,
  Text,
} from '@cowbell/ui';
import {
  downloadLossRunsDocument,
  generateLossRunsDocument,
  sendLossRunsRequestEmail,
} from '../../../api';
import { useActor } from '../../hooks/useActor';
import { useAPIErrorHandler } from '../../hooks/useAPIErrorHandler';
import useNotifications from '../../../_lib/notifications/useNotifications';
import { getData } from '../../../utils/functional/fp';
import { saveBlobFile } from '../../../utils/appUtils';
import { downloadAWSDoc } from '../../../accounts/AccountService';
import { useAccountDetails } from '../../hooks/queries/accounts/useAccountDetails';
import useIsActiveClaimOnAccount from './BrokerLossRunsModal/useIsActiveClaimOnAccount';
import useAccountHasPolicies from './BrokerLossRunsModal/useAccountHasPolicies';

const MODAL_Z_INDEX = 1400;
/** The generated document is not ready the moment the generate call returns. */
export const DOWNLOAD_DELAY_MS = 5000;

interface BrokerLossRunsModalProps {
  readonly data: {
    readonly accountId: string;
    readonly policyNumber?: string;
  };
  readonly close: () => void;
  /** The modal host's open state. */
  readonly open?: boolean;
}

interface ModalViewProps {
  readonly accountName: string;
  readonly accountId: string;
  readonly policyNumber?: string;
  readonly closeModal: () => void;
}

interface LossRunsDownloadInfo {
  readonly urls: string[];
  readonly errorMessage?: string;
}

/** The download response is a `{ urls }` object, an array of urls, or a single url. */
export function lossRunsDownloadInfo(docInfo: unknown): LossRunsDownloadInfo {
  if (Array.isArray(docInfo)) {
    return { urls: docInfo };
  }
  if (docInfo && typeof docInfo === 'object') {
    const { urls, errorMessage } = docInfo as {
      urls?: unknown;
      errorMessage?: string;
    };
    return { urls: Array.isArray(urls) ? urls : [], errorMessage };
  }
  return { urls: docInfo ? [docInfo as string] : [] };
}

/** The first file has no suffix; the rest are numbered from 2. */
export const lossRunsFileName = (accountName: string, index: number) =>
  `LossRuns_${accountName}${index === 0 ? '' : `_${index + 1}`}`;

const BrokerLossRunsModal = ({
  data,
  close,
  open = true,
}: BrokerLossRunsModalProps) => {
  const accountDetailsQuery = useAccountDetails(data.accountId);
  const isActiveClaimOnAccount = useIsActiveClaimOnAccount({
    accountId: data.accountId,
  });
  const accountHasPolicies = useAccountHasPolicies(data.accountId);

  return (
    <UiModal
      opened={open}
      onClose={close}
      title="Loss Runs"
      size="md"
      centered
      zIndex={MODAL_Z_INDEX}
    >
      <LossRunsBody
        data={data}
        close={close}
        accountName={accountDetailsQuery.data?.name}
        isLoading={
          accountDetailsQuery.data == null ||
          isActiveClaimOnAccount.isLoading ||
          accountHasPolicies == null
        }
        isActiveClaim={isActiveClaimOnAccount.isActiveClaim === true}
        accountHasPolicies={accountHasPolicies}
      />
    </UiModal>
  );
};

interface LossRunsBodyProps {
  readonly data: BrokerLossRunsModalProps['data'];
  readonly close: () => void;
  readonly accountName: string;
  readonly isLoading: boolean;
  readonly isActiveClaim: boolean;
  readonly accountHasPolicies: boolean | undefined;
}

const LossRunsBody = ({
  data,
  close,
  accountName,
  isLoading,
  isActiveClaim,
  accountHasPolicies,
}: LossRunsBodyProps) => {
  if (isLoading) {
    return <LoadingSkeleton />;
  }
  if (accountHasPolicies === false) {
    return (
      <NoPoliciesForAccountView
        accountId={data.accountId}
        accountName={accountName}
        closeModal={close}
      />
    );
  }
  if (isActiveClaim) {
    return (
      <RequestLossRunsModalView
        accountId={data.accountId}
        accountName={accountName}
        policyNumber={data.policyNumber}
        closeModal={close}
      />
    );
  }
  return (
    <GenerateLossRunsModalView
      accountId={data.accountId}
      accountName={accountName}
      closeModal={close}
    />
  );
};

export const BrokerLossRunsModalConfig = {
  BrokerLossRunsModal: {
    component: BrokerLossRunsModal,
    config: {
      title: 'Loss Runs',
      override: true,
    },
  },
};

const RequestLossRunsModalView = (props: ModalViewProps) => {
  const handleAPIError = useAPIErrorHandler();
  const actor = useActor();

  const [isRequestComplete, setIsRequestComplete] = React.useState(false);

  const handleRequestClick = () => {
    setIsRequestComplete(false);

    sendLossRunsRequestEmail({
      data: {
        requestorName: actor.fullName,
        requestorEmail: actor.email,
        accountName: props.accountName,
        policyNumber: props.policyNumber,
      },
    })
      .then(() => {
        setIsRequestComplete(true);
      })
      .catch(
        handleAPIError('Unable to send Loss Runs request email at this time')
      );
  };

  if (isRequestComplete) {
    return (
      <Stack gap="md">
        <Text ta="center">
          Great! A request has been sent. A Cowbell representative will follow
          up with you as soon as possible.
        </Text>
        <Group justify="center">
          <Button onClick={props.closeModal}>Close</Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Text ta="center">
        A Cowbell team member will need to assist with this. Would you like to
        proceed with the request for Loss Runs?
      </Text>
      <Group justify="center">
        <Button variant="default" onClick={props.closeModal}>
          Close
        </Button>
        <Button onClick={handleRequestClick}>Request</Button>
      </Group>
    </Stack>
  );
};

const GenerateLossRunsModalView = (props: ModalViewProps) => {
  const handleAPIError = useAPIErrorHandler();
  const notifications = useNotifications();
  const actor = useActor();

  const [downloadOnComplete, setDownloadOnComplete] = React.useState(false);
  const [emailAgentOnComplete, setEmailAgentOnComplete] = React.useState(false);

  const [isRequestActive, setIsRequestActive] = React.useState(false);
  const [isRequestComplete, setIsRequestComplete] = React.useState(false);

  const downloadDocuments = async () => {
    try {
      const docInfo = await downloadLossRunsDocument(props.accountId).then(
        getData
      );
      const { urls, errorMessage } = lossRunsDownloadInfo(docInfo);

      if (errorMessage) {
        notifications.warning(errorMessage);
      }

      for (let i = 0; i < urls.length; i++) {
        const fileURL = await downloadAWSDoc(urls[i]);
        saveBlobFile(fileURL, lossRunsFileName(props.accountName, i), '.pdf');
      }
    } catch {
      // The generate call succeeded; a failed download is not reported.
    }
  };

  const handleGenerateClick = async () => {
    setIsRequestActive(true);

    try {
      await generateLossRunsDocument({
        data: {
          accountId: props.accountId,
          sendEmailToAgent: emailAgentOnComplete,
          sendEmailToPH: false,
          email: actor.email,
          firstName: actor.firstName,
          lastName: actor.lastName,
        },
      });

      setIsRequestComplete(true);

      if (downloadOnComplete) {
        setTimeout(downloadDocuments, DOWNLOAD_DELAY_MS);
      }
    } catch (e) {
      handleAPIError('Unable to request loss run generation at this time')(
        e as Error
      );
    } finally {
      setIsRequestActive(false);
    }
  };

  if (isRequestComplete) {
    return (
      <Stack gap="md">
        {downloadOnComplete && (
          <Text ta="center">
            The Loss Runs file will download automatically momentarily.
          </Text>
        )}
        {emailAgentOnComplete && (
          <Text ta="center">
            An email with the requested Loss Runs will be emailed to{' '}
            <em>{actor.email}</em> momentarily.
          </Text>
        )}
        <Group justify="center">
          <Button variant="default" onClick={props.closeModal}>
            Close
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Text ta="center">Generating Loss Runs may take a moment...</Text>
      <Group justify="center" gap="md">
        <Checkbox
          label="Automatically download when ready"
          checked={downloadOnComplete}
          onChange={(event) =>
            setDownloadOnComplete(event.currentTarget.checked)
          }
        />
        <Checkbox
          label="Email me when ready"
          checked={emailAgentOnComplete}
          onChange={(event) =>
            setEmailAgentOnComplete(event.currentTarget.checked)
          }
        />
      </Group>
      <Group justify="center">
        <Button variant="default" onClick={props.closeModal}>
          Close
        </Button>
        <Button
          onClick={handleGenerateClick}
          loading={isRequestActive}
          disabled={
            isRequestActive || !(downloadOnComplete || emailAgentOnComplete)
          }
        >
          Generate
        </Button>
      </Group>
    </Stack>
  );
};

const NoPoliciesForAccountView = (props: ModalViewProps) => (
  <Stack gap="md">
    <Text ta="center">
      No policies found for <em>{props.accountName}</em>.
    </Text>
    <Group justify="center">
      <Button variant="default" onClick={props.closeModal}>
        Close
      </Button>
    </Group>
  </Stack>
);

const LoadingSkeleton = () => (
  <Stack gap="md" aria-busy="true">
    <Skeleton height="5rem" />
    <Group justify="center">
      <Skeleton width="5rem" height="2.5rem" />
      <Skeleton width="5rem" height="2.5rem" />
    </Group>
  </Stack>
);
